import { isNumberValue } from "@sameday/core/typePredicates";

import { getCurrentRoutePattern } from "./pageviewTracker";

/**
 * Reload-rage detection (SD-10021): repeatedly refreshing the same page in a
 * short window is the "it's broken, reload harder" frustration signal that
 * autocapture can't see — rage/dead clicks only cover in-page clicking.
 *
 * Detection runs once per page load (App mount). Because capture is opted out
 * until company gating opts in a beat later, a direct capture() here would be
 * silently dropped — so a triggered rage is recorded as a pending marker
 * (same pattern as the pending login in posthog.ts) and captured by
 * usePostHogCompanyGating once opted in.
 */

/** Same-path reloads within this window count toward a rage burst. */
export const RAGE_WINDOW_MS = 60_000;
/** Reloads of the same path within the window that trigger the event. */
export const RAGE_THRESHOLD = 3;

const HISTORY_KEY = "ph_reload_history";
const PENDING_RAGE_KEY = "ph_pending_refresh_rage";
// A pending rage that never gets opted in (disabled company) must not
// resurface much later in the session; the enabled-company path consumes
// within seconds of load. Mirrors PENDING_LOGIN_TTL_MS in posthog.ts.
const PENDING_RAGE_TTL_MS = 2 * 60 * 1000;

export type ReloadEntry = {
  path: string;
  ts: number;
};

type ReloadRageResult = {
  /** Updated history to persist (pruned; cleared for `path` on a trigger). */
  entries: ReloadEntry[];
  /** Whether this reload crossed the rage threshold. */
  rage: boolean;
  /** Reloads of `path` inside the window, including this one. */
  count: number;
};

/**
 * Pure core: prune entries outside the window, append this reload, and check
 * the same-path threshold. On a trigger the path's entries are cleared so a
 * continued reload spree emits one event per {@link RAGE_THRESHOLD} burst —
 * not one per reload past the threshold.
 */
export function recordReload(
  entries: ReloadEntry[],
  path: string,
  now: number,
): ReloadRageResult {
  const fresh = entries.filter((entry) => now - entry.ts < RAGE_WINDOW_MS);
  fresh.push({ path, ts: now });
  const count = fresh.reduce(
    (total, entry) => (entry.path === path ? total + 1 : total),
    0,
  );
  if (count >= RAGE_THRESHOLD) {
    return {
      entries: fresh.filter((entry) => entry.path !== path),
      rage: true,
      count,
    };
  }
  return { entries: fresh, rage: false, count };
}

type PendingRefreshRage = {
  route_pattern: string;
  reload_count: number;
  ts: number;
};

function markPendingRefreshRage(pattern: string, count: number): void {
  try {
    const pending: PendingRefreshRage = {
      route_pattern: pattern,
      reload_count: count,
      ts: Date.now(),
    };
    sessionStorage.setItem(PENDING_RAGE_KEY, JSON.stringify(pending));
  } catch {
    // sessionStorage unavailable — lose this signal rather than crash load.
  }
}

/**
 * Read and clear the pending rage event, if fresh. Called by the gating hook
 * once capture is opted in (a load-time capture would be dropped while the
 * SDK is still opted out).
 */
export function consumePendingRefreshRage(): {
  route_pattern: string;
  reload_count: number;
} | null {
  try {
    const raw = sessionStorage.getItem(PENDING_RAGE_KEY);
    if (!raw) {
      return null;
    }
    // Consume-first, validate-after: clear the marker before the TTL/parse
    // checks so a stale or malformed entry can't persist and resurface on a
    // later consume. A rage that fails validation here is intentionally
    // dropped rather than retried — it's a once-per-burst best-effort signal.
    sessionStorage.removeItem(PENDING_RAGE_KEY);
    const { route_pattern, reload_count, ts } = JSON.parse(
      raw,
    ) as Partial<PendingRefreshRage>;
    if (
      !route_pattern ||
      !isNumberValue(reload_count) ||
      !isNumberValue(ts) ||
      Date.now() - ts > PENDING_RAGE_TTL_MS
    ) {
      return null;
    }
    return { route_pattern, reload_count };
  } catch {
    return null;
  }
}

// Once per page load: the App-mount effect that calls this double-runs under
// React StrictMode in dev, which would double-count every reload.
let detectionRan = false;

/**
 * Run reload-rage detection for this page load. Call once on App mount (NOT
 * main.tsx — keeps this module conflict-free with the siblings that wired
 * main). No-ops unless the browser reports this load as a reload.
 */
export function detectRefreshRageOnLoad(): void {
  if (detectionRan) {
    return;
  }
  detectionRan = true;
  let isReload = false;
  try {
    const [navigation] = performance.getEntriesByType("navigation");
    isReload =
      navigation instanceof PerformanceNavigationTiming &&
      navigation.type === "reload";
  } catch {
    // performance API unavailable — treat as not-a-reload.
  }
  if (!isReload) {
    return;
  }

  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    const entries: ReloadEntry[] = raw ? JSON.parse(raw) : [];
    const result = recordReload(
      Array.isArray(entries) ? entries : [],
      window.location.pathname,
      Date.now(),
    );
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(result.entries));
    if (result.rage) {
      // Report the normalized route pattern (SD-10020), never the raw path —
      // entity IDs stay out of event properties. Keying above still uses the
      // raw pathname so only same-literal-page reloads count together.
      markPendingRefreshRage(
        getCurrentRoutePattern() ?? "unknown",
        result.count,
      );
    }
  } catch {
    // Corrupt/unavailable storage — skip detection for this load.
  }
}

/** Test-only: allow re-running detection between cases. */
export function resetRefreshRageDetectionForTests(): void {
  detectionRan = false;
}
